<script setup>
import { required, integer } from "@vuelidate/validators";
import Button from "@/components/atoms/button";
import FormGroup from "@/components/molecules/form-group";
import FormInput from "@/components/molecules/form-input";
import Form from "@/components/molecules/form";
import { useFormValidation } from "@/components/organisms/mixins/form-validation.mixin";
import { useVuelidate } from "@vuelidate/core";

const props = defineProps({
  product: {
    type: Object,
    required: true
  },
  gelluleSize: {
    type: String,
    required: false,
    default: "md"
  }
});

const emit = defineEmits(["setPrice"]);

const priceAmount = ["20", "50", "80", "100", "150"];
const isInputtedPrice = ref(false);

const price = ref(null);

const { getState, getErrorMessage, checkValidation, states } =
  useFormValidation(props);

const filterPossibleValue = computed(() => {
  return priceAmount.filter((p) => {
    return (
      p >= props.product.attributes.config_prix.min &&
      p <= props.product.attributes.config_prix.max
    );
  });
});

const montantAsFloat = computed(() => {
  return parseFloat(price.value.replace(",", "."));
});

const montantIsNan = computed(() => {
  return isNaN(montantAsFloat.value);
});

const validations = computed(() => {
  return {
    price: {
      required,
      integer,
      maxValue: () => {
        return montantIsNan.value
          ? true
          : montantAsFloat.value <=
              parseInt(props.product.attributes.config_prix.max);
      },
      minValue: () => {
        return montantIsNan.value
          ? true
          : montantAsFloat.value >=
              parseInt(props.product.attributes.config_prix.min);
      }
    }
  };
});

const v$ = useVuelidate(validations, { price }, { $autoDirty: false });

const validate = () => {
  if (checkValidation(v$.value)) {
    emit("setPrice", {
      price: parseInt(montantAsFloat.value),
      isInputtedPrice: isInputtedPrice.value
    });
  }
};

defineExpose({
  validate
});
</script>

<template>
  <div>
    <p class="u-mb-xs25 u-font-400 u-font-primary">
      Choisissez le montant que vous souhaitez
    </p>
    <div class="u-flex u-flex-wrap u-gap-xs u-mb-sm">
      <Button
        v-for="p in filterPossibleValue"
        :key="p"
        :size="gelluleSize"
        outline
        variant="primary"
        radius="md"
        :selected="price === p"
        @click="price = p; isInputtedPrice = false"
      >
        <span class="u-font-14@sm u-font-700 u-font-primary">{{ p }}€</span>
      </Button>
    </div>
    <p class="u-mb-xs">
      Ou saisissez un montant compris entre {{ product.attributes.config_prix.min }}€ et {{ product.attributes.config_prix.max }}€
    </p>
    <Form @submit.prevent="validate()">
      <FormGroup
        size="xs"
        :state="getState(v$,'price') === states.SUCCESS ? states.DEFAULT : getState(v$,'price')"
      >
        <FormInput
          v-model.trim="price"
          :placeholder="false"
          size="xs"
          suffix="€"
          aria-label="Montant carte cadeau"
          :state="getState(v$,'price')"
          @input="isInputtedPrice = true"
        />

        <template #message>
          <template v-if="v$.price.integer.$invalid">
            <p>Le montant d'une carte cadeau doit être un nombre entier</p>
          </template>
          <template v-else-if="v$.price.minValue.$invalid">
            <p>Le montant minimal d'une carte cadeau est de {{ product.attributes.config_prix.min }}€</p>
          </template>
          <template v-else-if="v$.price.maxValue.$invalid">
            <p>Le montant maximal d'une carte cadeau est de {{ product.attributes.config_prix.max }}€</p>
          </template>
          <template v-else>
            <p>{{ getErrorMessage('price', `Le montant d'une carte cadeau doit être compris entre ${product.attributes.config_prix.min}€ et ${product.attributes.config_prix.max}€`) }}</p>
          </template>
        </template>
      </FormGroup>
    </Form>
  </div>
</template>